import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Power, Bike, MapPin, Wallet, CheckCircle2, Package } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Loader } from "../../components/Loader";

export default function DriverDashboard() {
  const { user, refresh } = useAuth();
  const [online, setOnline] = useState(!!user?.driver_active);
  const [offer, setOffer] = useState(null);
  const [active, setActive] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      try { setOffer(await api("/driver/offer")); } catch { setOffer(null); }
      try { setActive(await api("/driver/active-orders")); } catch {}
      try { setWallet(await api("/driver/wallet")); } catch {}
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  const toggleOnline = async () => {
    setBusy(true);
    try {
      const next = !online;
      await api("/driver/online", { method: "PUT", body: JSON.stringify({ online: next }) });
      setOnline(next); refresh(); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const respondOffer = async (orderId, accept) => {
    setBusy(true);
    try {
      await api(`/driver/offer/${orderId}/${accept ? "accept" : "reject"}`, { method: "POST", body: "{}" });
      toast.success(accept ? "تم قبول الطلب" : "تم رفض الطلب");
      setOffer(null); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const doStep = async (orderId, step) => {
    const otp = window.prompt(step === "pickup" ? "أدخل رمز استلام الشحنة من البائع" : "أدخل رمز التسليم من العميل");
    if (!otp) return;
    setBusy(true);
    try {
      await api(`/driver/orders/${orderId}/${step}`, { method: "POST", body: JSON.stringify({ otp: otp.trim() }) });
      toast.success(step === "pickup" ? "تم استلام الشحنة" : "تم تسليم الطلب ✅");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (loading) return <Loader />;

  return (
    <div className="animate-fade-in-up max-w-2xl mx-auto">
      <h1 className="text-2xl font-black text-[#161629] mb-4">لوحة السائق</h1>

      <button data-testid="driver-online-toggle" disabled={busy} onClick={toggleOnline} className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-white ${online ? "bg-[#00D26A]" : "bg-[#9CA3AF]"}`}>
        <Power size={22} /> {online ? "أنت متصل — جاهز لاستقبال الطلبات" : "غير متصل — اضغط للاتصال"}
      </button>

      {wallet && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white rounded-2xl border border-[#E8EAF2] p-4"><div className="flex items-center gap-2 text-[#6B7280] text-sm"><Wallet size={16} /> الرصيد</div><p className="text-xl font-black text-[#161629] mt-1">{Number(wallet.balance || 0).toFixed(2)} د.ل</p></div>
          <div className="bg-white rounded-2xl border border-[#E8EAF2] p-4"><div className="flex items-center gap-2 text-[#6B7280] text-sm"><Bike size={16} /> مستحقات COD</div><p className="text-xl font-black text-[#FF3D5A] mt-1">{Number(wallet.cod_debt || 0).toFixed(2)} د.ل</p></div>
        </div>
      )}

      {offer && offer.order_id && (
        <div className="mt-5 bg-[#FFF7E0] border-2 border-[#FFB627] rounded-2xl p-5 animate-fade-in-up">
          <p className="font-black text-[#161629] flex items-center gap-2"><Package size={18} /> عرض توصيل جديد</p>
          <p className="text-sm text-[#6B7280] mt-1">{offer.product_name} • {offer.delivery_city}</p>
          <p className="font-black text-[#00D26A] mt-1">أجرة: {Number(offer.delivery_fee || 0).toFixed(2)} د.ل</p>
          <div className="flex gap-3 mt-3">
            <button onClick={() => respondOffer(offer.order_id, false)} className="flex-1 py-3 rounded-xl bg-white border border-[#FF3D5A] text-[#FF3D5A] font-black">رفض</button>
            <button onClick={() => respondOffer(offer.order_id, true)} className="flex-1 py-3 rounded-xl bg-[#00D26A] text-white font-black">قبول</button>
          </div>
        </div>
      )}

      <h2 className="font-black text-[#161629] mt-6 mb-3">الطلبات النشطة ({active.length})</h2>
      {active.length === 0 ? (
        <p className="text-center text-[#6B7280] py-8">لا توجد طلبات نشطة حالياً</p>
      ) : (
        <div className="space-y-3">
          {active.map((o) => (
            <div key={o.order_id} className="bg-white rounded-2xl border border-[#E8EAF2] p-4">
              <p className="font-black text-[#161629]">{o.product_name}</p>
              <p className="text-sm text-[#6B7280] flex items-center gap-1 mt-1"><MapPin size={14} /> {o.delivery_city} — {o.delivery_region || ""}</p>
              <p className="text-sm text-[#6B7280]">{o.delivery_name} • {o.delivery_phone}</p>
              <div className="flex gap-2 mt-3">
                {["accepted", "at_store"].includes(o.delivery_status) && <button onClick={() => doStep(o.order_id, "pickup")} className="flex-1 py-2.5 rounded-xl bg-[#3742FA] text-white font-black text-sm">استلام الشحنة</button>}
                {["picked_up", "on_the_way", "at_customer"].includes(o.delivery_status) && <button onClick={() => doStep(o.order_id, "deliver")} className="flex-1 py-2.5 rounded-xl bg-[#00D26A] text-white font-black text-sm flex items-center justify-center gap-1"><CheckCircle2 size={16} /> تسليم الطلب</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
